import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Select, MenuItem, Button,
  TextField, Stack, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadIncidents, createNewIncident, updateIncidentRecord, confirmContainment } from './incidentsSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';
import { fetchUsers } from '../../services/api/identityService';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const SEV_COLOR = { low: tokens.severity.low, medium: tokens.severity.medium, high: tokens.severity.high, critical: tokens.severity.critical };
const STATUS_OPTIONS = ['open', 'investigating', 'contained', 'resolved'];
const SEVERITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const emptyIncident = { title: '', description: '', severity: 'MEDIUM', sourceModule: 'MANUAL', openLinkedTicket: false };

export default function IncidentsPage() {
  const dispatch = useDispatch();
  const { items } = useSelector((s) => s.incidents);
  const { can, loaded } = useCurrentPermissions();
  const canWrite = can('incident:write');
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyIncident);
  const [error, setError] = useState(null);

  useEffect(() => { dispatch(loadIncidents()); fetchUsers().then(setUsers).catch(() => setUsers([])); }, [dispatch]);

  const open = items.filter((i) => i.status !== 'resolved').length;
  const critical = items.filter((i) => i.severity === 'critical' && i.status !== 'resolved').length;

  const handleCreate = async () => {
    if (!form.title.trim()) return setError('Title is required.');
    setError(null);
    const result = await dispatch(createNewIncident(form));
    if (createNewIncident.rejected.match(result)) {
      setError(result.error?.message || 'Could not create this incident.');
      return;
    }
    setDialogOpen(false);
    setForm(emptyIncident);
  };

  return (
    <Box>
      <PageHeader
        title="Incident Management"
        subtitle="SIEM Lite — triage workflow correlating findings from every detection module through to resolution"
        action={<Button variant="contained" disabled={!canWrite} startIcon={<AddRoundedIcon />} onClick={() => { setForm(emptyIncident); setError(null); setDialogOpen(true); }}>Create Incident</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loaded && !canWrite && <Alert severity="info" sx={{ mb: 2 }}>Your role has read-only incident access. Ask an administrator for incident:write permission to create, assign, contain, or change incidents.</Alert>}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Total Incidents" value={items.length} icon={ListAltRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Open" value={open} accent={tokens.severity.medium} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Critical (Open)" value={critical} accent={tokens.severity.critical} /></Grid>
      </Grid>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ID', 'Title', 'Severity', 'Source', 'Assigned To', 'Status', 'Containment', 'Detected'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} hover>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem' }}>{i.id}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem', maxWidth: 280 }}>{i.title}</TableCell>
                <TableCell><Chip label={i.severity} size="small" sx={{ color: SEV_COLOR[i.severity], border: `1px solid ${SEV_COLOR[i.severity]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{i.sourceModule}</TableCell>
                <TableCell>
                  <Select size="small" value={i.assignedTo || ''} variant="standard" disableUnderline displayEmpty
                    sx={{ fontSize: '0.76rem', minWidth: 130 }}
                    disabled={!canWrite}
                    onChange={async (e) => {
                      if (!e.target.value) return;
                      const result = await dispatch(updateIncidentRecord({ id: i.id, changes: { assignedToUserId: e.target.value } }));
                      if (updateIncidentRecord.rejected.match(result)) setError(result.error?.message || 'Incident assignment could not be changed.');
                    }}>
                    <MenuItem value="" disabled sx={{ fontSize: '0.78rem' }}>Unassigned</MenuItem>
                    {users.filter((u) => String(u.status || '').toLowerCase() === 'active').map((u) => (
                      <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.78rem' }}>{u.fullName || u.name || u.email}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    disabled={!canWrite}
                    size="small" value={i.status} variant="standard" disableUnderline
                    sx={{ fontSize: '0.76rem' }}
                    onChange={async (e) => {
                      const nextStatus = e.target.value;
                      if (String(nextStatus).toLowerCase() === 'contained' && !i.containmentConfirmedAt) {
                        const evidence = window.prompt('Containment evidence is required (minimum 10 characters). Enter the verified response evidence:');
                        if (!evidence || evidence.trim().length < 10) return;
                        const result = await dispatch(confirmContainment({ id: i.id, evidence: evidence.trim() }));
                        if (confirmContainment.rejected.match(result)) {
                          setError(result.error?.message || 'Containment could not be confirmed.');
                          return;
                        }
                        // confirm-containment atomically records the evidence and moves an
                        // OPEN/INVESTIGATING incident to CONTAINED. Do not issue a second
                        // status PATCH against the just-updated record.
                        return;
                      }
                      const result = await dispatch(updateIncidentRecord({ id: i.id, changes: { status: nextStatus } }));
                      if (updateIncidentRecord.rejected.match(result)) {
                        setError(result.error?.message || 'Incident status could not be changed.');
                      }
                    }}
                  >
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>{s.replace('_', ' ')}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell>
                  {i.containmentConfirmedAt ? (
                    <Chip label="confirmed" size="small" />
                  ) : (
                    <Button size="small" variant="outlined" disabled={!canWrite} onClick={async () => {
                      const evidence = window.prompt('Enter containment evidence (minimum 10 characters). Confirm only after verifying the response action actually succeeded.');
                      if (!evidence || evidence.trim().length < 10) return;
                      const result = await dispatch(confirmContainment({ id: i.id, evidence: evidence.trim() }));
                      if (confirmContainment.rejected.match(result)) setError(result.error?.message || 'Containment could not be confirmed.');
                    }}>Confirm</Button>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(i.detectedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {dialogOpen && (
      <Dialog open onClose={() => { setDialogOpen(false); setForm(emptyIncident); }} maxWidth="sm" fullWidth>
        <DialogTitle>Create Incident</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" size="small" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} autoFocus />
            <TextField label="Description" size="small" multiline minRows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <TextField select label="Severity" size="small" value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>
              {SEVERITY_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField label="Source" size="small" value={form.sourceModule} onChange={(e) => setForm((f) => ({ ...f, sourceModule: e.target.value }))} helperText="e.g. MANUAL, THREAT_DETECTION, EMAIL_SECURITY" />
            <FormControlLabel
              control={<Checkbox checked={form.openLinkedTicket} onChange={(e) => setForm((f) => ({ ...f, openLinkedTicket: e.target.checked }))} />}
              label="Also open a linked support ticket"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogOpen(false); setForm(emptyIncident); }}>Cancel</Button>
          <Button variant="contained" disabled={!canWrite} onClick={handleCreate}>Create Incident</Button>
        </DialogActions>
      </Dialog>
      )}
    </Box>
  );
}
